import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import CkEditorContent from "src/components/content/ck-editor-content";
import { Button } from "src/components/ui/button";
import {
  servicesQueryOptions,
  finishedReferencesQueryOptions,
} from "src/react-query/react-query";
import { seo } from "src/utils/seo";
import { ReferenceBox } from "~/components/references/ReferenceBox";

export const Route = createFileRoute("/_breadcrumbs/sluzby")({
  component: RouteComponent,
  loader: async ({ context }) => {
    const services = await context.queryClient.ensureQueryData(
      servicesQueryOptions(),
    );

    if (!services) {
      throw new Error("Žádné služby nebyly nalezeny");
    }

    const references = await Promise.all(
      services.services.map(async (service) => {
        if (!service.category?.slug) return null;

        const references = await context.queryClient.ensureQueryData(
          finishedReferencesQueryOptions({
            categorySlug: service.category.slug,
            limit: 4,
          }),
        );

        return {
          references,
          categorySlug: service.category.slug,
        };
      }),
    );

    return { crumb: "Služby", services, references };
  },
  head: () => {
    return {
      meta: [
        ...seo({
          title: `Služby | VESAS`,
        }),
      ],
    };
  },
});

function RouteComponent() {
  const { services, references } = Route.useLoaderData();

  if (!services) {
    return <div>Žádné služby nebyly nalezeny</div>;
  }

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const anchorElement = document.querySelector(hash);
    if (!anchorElement) return;
    setTimeout(() => {
      anchorElement.scrollIntoView();
    }, 0);
  }, []);

  return (
    <div
      className={"mt-8 flex flex-col gap-32 px-4 md:px-12 lg:px-16 xl:px-24"}
    >
      {services.services.map((service) => (
        <div key={service.id} className={"flex flex-col gap-4"}>
          <h2
            className="flex scroll-mt-24 text-4xl whitespace-pre-wrap"
            id={service.category?.slug}
          >
            <Link
              to={`/sluzby`}
              hash={service.category?.slug}
              className="group relative flex items-center border-none lg:-ml-2 lg:pl-2"
            >
              <span className="absolute -ml-8 hidden items-center border-0 opacity-0 group-hover:opacity-100 group-focus:opacity-100 lg:flex">
                <span className="flex h-6 w-6 items-center justify-center rounded-md text-slate-400 shadow-sm ring-1 ring-slate-900/5 hover:text-slate-700 hover:shadow hover:ring-slate-900/10 dark:bg-slate-800 dark:text-slate-400 dark:shadow-none dark:ring-0 dark:hover:bg-slate-700 dark:hover:text-slate-200">
                  <svg width="12" height="12" fill="none" aria-hidden="true">
                    <path
                      d="M3.75 1v10M8.25 1v10M1 3.75h10M1 8.25h10"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    ></path>
                  </svg>
                </span>
              </span>
              {service.category?.name}
            </Link>
          </h2>
          <div className="flex flex-col gap-4 md:flex-row md:gap-12">
            <CkEditorContent
              className="flex-2"
              htmlContent={service.content ?? ""}
            />
            {references && (
              <div className="flex-3">
                <div className="mb-12 flex flex-row flex-wrap justify-center gap-4">
                  {references
                    ?.filter(
                      (reference) =>
                        reference?.categorySlug === service.category?.slug,
                    )[0]
                    ?.references?.map((reference) => (
                      <ReferenceBox
                        className="h-64 max-w-84"
                        mode="compact"
                        key={reference.id}
                        reference={reference}
                      />
                    ))}
                </div>

                {service.category &&
                  (references?.filter(
                    (reference) =>
                      reference?.categorySlug === service.category?.slug,
                  )[0]?.references?.length || 0) > 0 && (
                    <Button
                      asChild
                      variant={"outline"}
                      className={"mx-auto block w-min"}
                    >
                      <Link
                        to={`/reference/kategorie/$categorySlug`}
                        params={{
                          categorySlug: service.category.slug,
                        }}
                      >
                        Zobrazit všechny reference
                      </Link>
                    </Button>
                  )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
